import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { HeroCarousel } from '../components/home/HeroCarousel';
import { FloatingCTA } from '../components/home/FloatingCTA';
import HomepagePopup from '../components/HomepagePopup';
import { WebcamCard } from '../components/home/WebcamCard';
import { TrafficCard } from '../components/home/TrafficCard';
import { SEO } from '../components/SEO';
import { PAGE_SEO } from '../config/seoCopy';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, MapPin, Phone, Calendar, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Default featured sections (fallback)
const defaultFeaturedSections = [
  {
    id: 'el-camp',
    title: { ca: 'El Camp', es: 'El Campo', fr: 'Le Parcours', en: 'The Course' },
    description: {
      ca: 'Descobreix els nostres 9 forats amb vistes espectaculars als Pirineus',
      es: 'Descubre nuestros 9 hoyos con vistas espectaculares a los Pirineos',
      fr: 'Découvrez nos 9 trous avec des vues spectaculaires sur les Pyrénées',
      en: 'Discover our 9 holes with spectacular views of the Pyrenees'
    },
    image: 'https://images.unsplash.com/photo-1587174486073-ae5e5cff23aa?q=80&w=800&auto=format&fit=crop',
    link: '/el-camp'
  },
  {
    id: 'restaurant',
    title: { ca: 'Restaurant', es: 'Restaurante', fr: 'Restaurant', en: 'Restaurant' },
    description: {
      ca: 'Gastronomia de proximitat amb productes locals de qualitat',
      es: 'Gastronomía de proximidad con productos locales de calidad',
      fr: 'Gastronomie de proximité avec des produits locaux de qualité',
      en: 'Local gastronomy with quality local products'
    },
    image: 'https://images.unsplash.com/photo-1768697358705-c1b60333da35?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
    link: '/restaurant'
  },
  {
    id: 'allotjament',
    title: { ca: 'Allotjament', es: 'Alojamiento', fr: 'Hébergement', en: 'Accommodation' },
    description: {
      ca: "Allotjament còmode per gaudir d'una estada completa",
      es: 'Alojamiento cómodo para disfrutar de una estancia completa',
      fr: 'Hébergement confortable pour profiter d\'un séjour complet',
      en: 'Comfortable accommodation for a complete stay'
    },
    image: 'https://images.unsplash.com/photo-1566073771259-6a8506099945?q=80&w=800&auto=format&fit=crop',
    link: '/allotjament'
  }
];

// Default homepage content
const defaultHomeContent = {
  welcome_pretitle: { ca: 'UGOLF ARAVELL Andorra', es: 'UGOLF ARAVELL Andorra', fr: 'UGOLF ARAVELL Andorra', en: 'UGOLF ARAVELL Andorra' },
  welcome_title: { ca: 'Golf als Pirineus', es: 'Golf en los Pirineos', fr: 'Golf aux Pyrénées', en: 'Golf in the Pyrenees' },
  welcome_subtitle: { ca: "Descobreix l'experiència única de jugar a golf en un entorn natural privilegiat, amb vistes impressionants a les muntanyes dels Pirineus.", es: 'Descubre la experiencia única de jugar al golf en un entorno natural privilegiado, con vistas impresionantes a las montañas de los Pirineos.', fr: "Découvrez l'expérience unique de jouer au golf dans un environnement naturel privilégié, avec des vues impressionnantes sur les montagnes des Pyrénées.", en: 'Discover the unique experience of playing golf in a privileged natural setting, with impressive views of the Pyrenees mountains.' },
  welcome_image: 'https://images.unsplash.com/photo-1662718552420-f612c0408be8?crop=entropy&cs=srgb&fm=jpg&q=85&w=800',
  welcome_cta_text: { ca: 'Reserva ara', es: 'Reserva ahora', fr: 'Réserver maintenant', en: 'Book now' },
  welcome_cta_link: 'https://open.teeone.golf/es/aravell',
  webcam_pretitle: { ca: 'Informació en temps real', es: 'Información en tiempo real', fr: 'Information en temps réel', en: 'Real-time information' },
  webcam_title: { ca: 'Estat del camp i accessos', es: 'Estado del campo y accesos', fr: 'État du parcours et accès', en: 'Course conditions & access' },
  webcam_subtitle: { ca: "Consulta les condicions actuals del camp, la meteorologia i l'estat de les carreteres abans de venir.", es: 'Consulta las condiciones actuales del campo, la meteorología y el estado de las carreteras antes de venir.', fr: "Consultez les conditions actuelles du parcours, la météo et l'état des routes avant de venir.", en: 'Check current course conditions, weather and road status before coming.' },
  featured_pretitle: { ca: 'Descobreix', es: 'Descubre', fr: 'Découvrez', en: 'Discover' },
  featured_title: { ca: 'Experiència Completa', es: 'Experiencia Completa', fr: 'Expérience Complète', en: 'Complete Experience' },
  location_pretitle: { ca: 'On Som', es: 'Dónde Estamos', fr: 'Où Nous Trouver', en: 'Where We Are' },
  location_title: { ca: 'Al cor dels Pirineus', es: 'En el corazón de los Pirineos', fr: 'Au cœur des Pyrénées', en: 'In the heart of the Pyrenees' },
  location_description: { ca: "Situat a Aravell, a la frontera entre Catalunya i Andorra, el nostre camp ofereix una experiència única envoltada de natura i paisatges de muntanya.", es: 'Situado en Aravell, en la frontera entre Cataluña y Andorra, nuestro campo ofrece una experiencia única rodeada de naturaleza y paisajes de montaña.', fr: "Situé à Aravell, à la frontière entre la Catalogne et l'Andorre, notre parcours offre une expérience unique entourée de nature et de paysages de montagne.", en: 'Located in Aravell, on the border between Catalonia and Andorra, our course offers a unique experience surrounded by nature and mountain landscapes.' }
};

const HomePage = () => {
  const { t, language } = useLanguage();
  const [homeContent, setHomeContent] = useState(defaultHomeContent);
  const [featuredSections, setFeaturedSections] = useState(defaultFeaturedSections);
  const [siteConfig, setSiteConfig] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        // Fetch all content in parallel
        const [homeRes, featuredRes, configRes] = await Promise.all([
          fetch(`${API_URL}/api/homepage-content`),
          fetch(`${API_URL}/api/featured-sections?active_only=true`),
          fetch(`${API_URL}/api/public/config`).catch(() => null)
        ]);

        if (homeRes.ok) {
          const data = await homeRes.json();
          // Merge with defaults to ensure all fields exist
          setHomeContent(prev => ({ ...prev, ...data }));
        }

        if (featuredRes.ok) {
          const sections = await featuredRes.json();
          if (sections && sections.length > 0) {
            setFeaturedSections(sections);
          }
        }

        if (configRes && configRes.ok) {
          const config = await configRes.json();
          setSiteConfig(config);
        }
      } catch (error) {
        console.error('Failed to fetch homepage content:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchContent();
  }, []);

  // Helper to get text in current language with fallback
  const getText = (obj, fallback = '') => {
    if (!obj) return fallback;
    return obj[language] || obj.ca || obj.es || fallback;
  };

  const phone = siteConfig?.phone || '+34 661 19 40 23';
  const mapsUrl = siteConfig?.maps_url || 'https://maps.app.goo.gl/tt4P5rYdVtHFwdsM8';
  const mapsEmbedUrl = siteConfig?.maps_embed_url || 'https://maps.google.com/maps?q=42.3604961,1.4052768&t=&z=15&ie=UTF8&iwloc=&output=embed';

  return (
    <main data-testid="home-page">
      <HomepagePopup />
      <SEO
        title={PAGE_SEO.home.title}
        description={PAGE_SEO.home.description}
      />
      {/* Hero Carousel */}
      <HeroCarousel />

      {/* Floating CTA */}
      <FloatingCTA />

      {/* Welcome Section */}
      <section className="section-spacing bg-white relative overflow-hidden">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
                {getText(homeContent.welcome_pretitle, 'UGOLF ARAVELL Andorra')}
              </p>
              <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl text-[#1A1A1A] font-medium tracking-tight mb-6">
                {getText(homeContent.welcome_title, t('home.heroTitle'))}
              </h2>
              <p className="text-[#666666] text-base lg:text-lg leading-relaxed mb-8">
                {getText(homeContent.welcome_subtitle, t('home.heroSubtitle'))}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={homeContent.welcome_cta_link || 'https://open.teeone.golf/es/aravell'}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-[#1A4D2E] text-white px-6 py-3 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#153d24] transition-colors duration-200"
                  data-testid="home-reserva-btn"
                >
                  <Calendar className="w-4 h-4" />
                  {t('nav.reserva')}
                </a>
                <Link
                  to="/el-camp"
                  className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-[#1A4D2E] text-[#1A4D2E] px-6 py-3 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#1A4D2E] hover:text-white transition-colors duration-200"
                  data-testid="home-elcamp-btn"
                >
                  {t('nav.elCamp')}
                  <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </motion.div>

            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img 
                  src={homeContent.welcome_image || 'https://images.unsplash.com/photo-1662718552420-f612c0408be8?crop=entropy&cs=srgb&fm=jpg&q=85&w=800'}
                  alt="Golf at Aravell"
                  className="w-full h-full object-cover"
                  loading="lazy"
                />
              </div>
              {/* Decorative element */}
              <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-[#D4AF37]/10 -z-10" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Webcam & Traffic Section - Estat del camp i accessos */}
      <section className="section-spacing bg-white" data-testid="webcam-section">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
              {getText(homeContent.webcam_pretitle, 'Informació en temps real')}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl text-[#1A1A1A] font-medium tracking-tight mb-4">
              {getText(homeContent.webcam_title, 'Estat del camp i accessos')}
            </h2>
            <p className="text-[#666666] max-w-2xl mx-auto">
              {getText(homeContent.webcam_subtitle, "Consulta les condicions actuals del camp, la meteorologia i l'estat de les carreteres abans de venir.")}
            </p>
          </motion.div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            <WebcamCard />
            <TrafficCard />
          </div>
        </div>
      </section>

      {/* Featured Sections */}
      <section className="section-spacing bg-[#F9F9F7]" data-testid="featured-sections">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
              {getText(homeContent.featured_pretitle, 'Descobreix')}
            </p>
            <h2 className="font-display text-3xl sm:text-4xl text-[#1A1A1A] font-medium tracking-tight">
              {getText(homeContent.featured_title, 'Experiència Completa')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {featuredSections.map((section, index) => (
              <motion.div
                key={section.id}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Link 
                  to={section.link}
                  className="group block bg-white overflow-hidden border border-[#E5E5E5] hover:border-[#1A4D2E] transition-colors duration-300"
                  data-testid={`featured-${section.id}`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img 
                      src={section.image}
                      alt={getText(section.title, '')}
                      className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  </div>
                  <div className="p-6">
                    <h3 className="font-display text-xl text-[#1A1A1A] font-medium mb-2 group-hover:text-[#1A4D2E] transition-colors duration-200">
                      {getText(section.title, '')}
                    </h3>
                    <p className="text-sm text-[#666666] line-clamp-2 mb-4">
                      {getText(section.description, '')}
                    </p>
                    <span className="inline-flex items-center gap-2 text-xs font-semibold tracking-[0.1em] uppercase text-[#1A4D2E]">
                      {t('common.veureMes')}
                      <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                    </span>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Location & Contact CTA */}
      <section className="section-spacing bg-[#1A4D2E] text-white" data-testid="location-section">
        <div className="container-custom">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <p className="text-xs font-semibold tracking-[0.3em] uppercase text-[#D4AF37] mb-4">
                {getText(homeContent.location_pretitle, 'On Som')}
              </p>
              <h2 className="font-display text-3xl sm:text-4xl font-medium tracking-tight mb-6">
                {getText(homeContent.location_title, 'Al cor dels Pirineus')}
              </h2>
              <p className="text-white/80 mb-8 leading-relaxed">
                {getText(homeContent.location_description, "Situat a Aravell, a la frontera entre Catalunya i Andorra, el nostre camp ofereix una experiència única envoltada de natura i paisatges de muntanya.")}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <a
                  href={`tel:${phone.replace(/\s/g, '')}`}
                  className="inline-flex items-center justify-center gap-2 bg-white text-[#1A4D2E] px-6 py-3 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-[#F0F2EF] transition-colors duration-200"
                  data-testid="location-phone-btn"
                >
                  <Phone className="w-4 h-4" />
                  {phone}
                </a>
                <a
                  href={mapsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-2 bg-transparent border-2 border-white text-white px-6 py-3 text-xs font-semibold tracking-[0.15em] uppercase hover:bg-white hover:text-[#1A4D2E] transition-colors duration-200"
                  data-testid="location-map-btn"
                >
                  <MapPin className="w-4 h-4" />
                  {language === 'ca' ? 'Com arribar' : language === 'es' ? 'Cómo llegar' : language === 'fr' ? 'Comment y arriver' : 'How to get there'}
                </a>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="relative aspect-video overflow-hidden rounded-lg"
            >
              <img
                src="https://customer-assets.emergentagent.com/job_c2dde011-664e-4252-813e-859a2b30c411/artifacts/rr832lrm_banderes%20campanar.png"
                alt="UGOLF ARAVELL Andorra - Club House"
                className="absolute inset-0 w-full h-full object-cover"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default HomePage;
